import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from './store';
import { loadUser } from './store/slices/authSlice';
import { fetchPlatformSettings } from './store/slices/platformSettingsSlice';
import { ProductFilterProvider } from './contexts/ProductFilterContext';
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import LandingPage from './pages/public/LandingPage';
import HomePage from './pages/public/HomePage';
import ProductsPageSimple from './pages/public/ProductsPageSimple';
import ProductDetailPage from './pages/public/ProductDetailPage';
import CategoryProductsPage from './pages/public/CategoryProductsPage';
import PublicShopPage from './pages/public/PublicShopPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import DashboardPage from './pages/protected/DashboardPage';
import ProfilePage from './pages/protected/ProfilePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagementPage from './pages/admin/UserManagementPage';
import UserRolesPage from './pages/admin/UserRolesPage';
import ShopManagementAdminPage from './pages/admin/ShopManagementPage';
import ShopApprovalsPage from './pages/admin/ShopApprovalsPage';
import ProductModerationPage from './pages/admin/ProductModerationPage';
import ProductManagementPage from './pages/admin/ProductManagementPage';
import OrderManagementPage from './pages/admin/OrderManagementPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import BusinessSettingsPage from './pages/admin/BusinessSettingsPage';
import CommissionSettingsPage from './pages/admin/CommissionSettingsPage';
import BusinessModelManagementPage from './pages/admin/BusinessModelManagementPage';
import CategoryManagementPage from './pages/admin/CategoryManagementPage';
import BrandManagementPage from './pages/admin/BrandManagementPage';
import ProductFieldConfigPage from './pages/admin/ProductFieldConfigPage';
import HomepageManagementPage from './pages/admin/HomepageManagementPage';
import LocationManagementPage from './pages/admin/LocationManagementPage';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import CartPage from './pages/buyer/CartPage';
import CheckoutPage from './pages/buyer/CheckoutPage';
import OrdersPage from './pages/buyer/OrdersPage';
import OrderDetailPage from './pages/buyer/OrderDetailPage';
import WishlistPage from './pages/buyer/WishlistPage';
import ReviewsPage from './pages/buyer/ReviewsPage';
import SellerDashboard from './pages/seller/SellerDashboard';
import ShopManagementPage from './pages/seller/ShopManagementPage';
import CreateShopPage from './pages/seller/CreateShopPage';
import SellerProductManagementPage from './pages/seller/SellerProductManagementPage';
import AddProductPage from './pages/seller/AddProductPage';
import SellerOrdersPage from './pages/seller/SellerOrdersPage';
import SellerOrderDetailPage from './pages/seller/SellerOrderDetailPage';
import SellerAnalyticsPage from './pages/seller/SellerAnalyticsPage';
import InventoryPage from './pages/seller/InventoryPage';
import CommissionPage from './pages/seller/CommissionPage';
import StaffProductsPage from './pages/staff/StaffProductsPage';
import StaffReviewsPage from './pages/staff/StaffReviewsPage';
import StaffSupportPage from './pages/staff/StaffSupportPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';

// Redirect component for dynamic routes
const BuyerOrderRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/buyer/orders/${id}`} replace />;
};

const SellerOrderRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/seller/orders/${id}`} replace />;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Load platform settings on app start
    dispatch(fetchPlatformSettings());

    // Load user on app start if token exists but user is not loaded
    const token = localStorage.getItem('token');
    const userFromStorage = localStorage.getItem('user');

    if (token && !isAuthenticated) {
      // If we have a token but not authenticated, try to load user
      dispatch(loadUser());
    } else if (token && userFromStorage && !isAuthenticated) {
      // If we have both token and user in storage but Redux state is not authenticated, load user
      dispatch(loadUser());
    }
  }, [dispatch, isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ProductFilterProvider onFiltersChange={(filters) => {
      // Handle filter changes here - this will be called when filters are applied
      console.log('Filters changed:', filters);
    }}>
      <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="products" element={<ProductsPageSimple />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="category/:categorySlug" element={<CategoryProductsPage />} />
        <Route path="category/:parentSlug/:childSlug" element={<CategoryProductsPage />} />
        <Route path="shop/:slug" element={<PublicShopPage />} />
        <Route path="shop/id/:id" element={<PublicShopPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="contact" element={<ContactPage />} />
      </Route>
      <Route path="/auth" element={<AuthLayout />}>
        <Route 
          path="login" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } 
        />
        <Route 
          path="signup" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPage />
          } 
        />
      </Route>

      {/* Legacy Route Redirects - Redirect old URLs to new dashboard structure */}
      <Route path="/super-admin" element={<Navigate to="/dashboard?role=super_admin" replace />} />
      <Route path="/super-admin/users" element={<Navigate to="/dashboard/super-admin/users?role=super_admin" replace />} />
      <Route path="/super-admin/user-roles" element={<Navigate to="/dashboard/super-admin/user-roles?role=super_admin" replace />} />
      <Route path="/super-admin/shops" element={<Navigate to="/dashboard/super-admin/shops?role=super_admin" replace />} />
      <Route path="/super-admin/products" element={<Navigate to="/dashboard/super-admin/products?role=super_admin" replace />} />
      <Route path="/super-admin/orders" element={<Navigate to="/dashboard/super-admin/orders?role=super_admin" replace />} />
      <Route path="/super-admin/analytics" element={<Navigate to="/dashboard/super-admin/analytics?role=super_admin" replace />} />
      <Route path="/super-admin/settings" element={<Navigate to="/dashboard/super-admin/settings?role=super_admin" replace />} />
      <Route path="/buyer" element={<Navigate to="/dashboard/buyer" replace />} />
      <Route path="/buyer/cart" element={<Navigate to="/dashboard/buyer/cart" replace />} />
      <Route path="/buyer/checkout" element={<Navigate to="/dashboard/buyer/checkout" replace />} />
      <Route path="/buyer/orders" element={<Navigate to="/dashboard/buyer/orders" replace />} />
      <Route path="/buyer/orders/:id" element={<BuyerOrderRedirect />} />
      <Route path="/buyer/wishlist" element={<Navigate to="/dashboard/buyer/wishlist" replace />} />
      <Route path="/buyer/reviews" element={<Navigate to="/dashboard/buyer/reviews" replace />} />
      <Route path="/seller" element={<Navigate to="/dashboard/seller" replace />} />
      <Route path="/seller/shop" element={<Navigate to="/dashboard/seller/shop" replace />} />
      <Route path="/seller/shop/create" element={<Navigate to="/dashboard/seller/shop/create" replace />} />
      <Route path="/seller/create-shop" element={<Navigate to="/dashboard/seller/shop/create" replace />} />
      <Route path="/seller/products" element={<Navigate to="/dashboard/seller/products" replace />} />
      <Route path="/seller/orders" element={<Navigate to="/dashboard/seller/orders" replace />} />
      <Route path="/seller/orders/:id" element={<SellerOrderRedirect />} />

      {/* Protected Routes with Sidebar */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Super Admin Routes */}
        <Route path="super-admin" element={
          <RoleProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/users" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <UserManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/user-roles" element={
          <RoleProtectedRoute allowedRoles={['super_admin']}>
            <UserRolesPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/shops" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ShopManagementAdminPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/shop-approvals" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ShopApprovalsPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/products" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ProductManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/product-moderation" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ProductModerationPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/orders" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <OrderManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/analytics" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <AdminAnalyticsPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/settings" element={
          <RoleProtectedRoute allowedRoles={['super_admin']}>
            <SystemSettingsPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/business-settings" element={
          <RoleProtectedRoute allowedRoles={['super_admin']}>
            <BusinessSettingsPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/commission-settings" element={
          <RoleProtectedRoute allowedRoles={['super_admin']}>
            <CommissionSettingsPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/business-models" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <BusinessModelManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/categories" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'staff']}>
            <CategoryManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/brands" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'staff']}>
            <BrandManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/locations" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <LocationManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/product-fields" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ProductFieldConfigPage />
          </RoleProtectedRoute>
        } />
        <Route path="super-admin/homepage" element={
          <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'staff']}>
            <HomepageManagementPage />
          </RoleProtectedRoute>
        } />

        {/* Buyer Routes */}
        <Route path="buyer" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <BuyerDashboard />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/cart" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <CartPage />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/checkout" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <CheckoutPage />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/orders" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <OrdersPage />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/orders/:id" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <OrderDetailPage />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/wishlist" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <WishlistPage />
          </RoleProtectedRoute>
        } />
        <Route path="buyer/reviews" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin', 'staff']}>
            <ReviewsPage />
          </RoleProtectedRoute>
        } />

        {/* Seller Routes */}
        <Route path="seller" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <SellerDashboard />
          </RoleProtectedRoute>
        } />
        <Route path="seller/shop" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <ShopManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/shop/create" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <CreateShopPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/products" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <SellerProductManagementPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/products/add" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <AddProductPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/products/edit/:id" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <AddProductPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/inventory" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <InventoryPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/orders" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <SellerOrdersPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/orders/:id" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <SellerOrderDetailPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/analytics" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <SellerAnalyticsPage />
          </RoleProtectedRoute>
        } />
        <Route path="seller/commission" element={
          <RoleProtectedRoute allowedRoles={['user', 'admin', 'super_admin']}>
            <CommissionPage />
          </RoleProtectedRoute>
        } />

        {/* Staff Routes */}
        <Route path="staff/products" element={
          <RoleProtectedRoute allowedRoles={['staff', 'admin', 'super_admin']}>
            <StaffProductsPage />
          </RoleProtectedRoute>
        } />
        <Route path="staff/reviews" element={
          <RoleProtectedRoute allowedRoles={['staff', 'admin', 'super_admin']}>
            <StaffReviewsPage />
          </RoleProtectedRoute>
        } />
        <Route path="staff/support" element={
          <RoleProtectedRoute allowedRoles={['staff', 'admin', 'super_admin']}>
            <StaffSupportPage />
          </RoleProtectedRoute>
        } />
      </Route>
    </Routes>
    </ProductFilterProvider>
  );
};

export default App;